import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Privacy Policy - ORIN';
  }, []);

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-card rounded-lg shadow-lg border border-border p-8">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              iconName="ArrowLeft"
            >
              Back to Login
            </Button>
          </div>

          <div className="prose prose-sm max-w-none">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mb-6">
              <strong>ORIN – Decision Intelligence System</strong><br />
              Effective Date: 1/1/2025<br />
              Last Updated: 9/1/2025
            </p>

            <p className="text-foreground mb-4">
              ORIN is a Decision Intelligence System developed and operated by American Logics LLC ("American Logics," "we," "our," or "us").
            </p>
            <p className="text-foreground mb-6">
              This Privacy Policy explains how we collect, use, protect, and process information when you access or use the ORIN platform ("Platform").
            </p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">1. Scope</h2>
            <p className="text-foreground mb-4">This Privacy Policy applies to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Users accessing ORIN through a client organization</li>
              <li>Administrators managing ORIN environments</li>
              <li>Visitors to ORIN login pages and related interfaces</li>
            </ul>
            <p className="text-foreground mb-6">ORIN is designed primarily for business and enterprise use.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">A. Account Information</h3>
            <p className="text-foreground mb-2">When users access ORIN, we may collect:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Name</li>
              <li>Business email address</li>
              <li>Organization name</li>
              <li>Role/title</li>
              <li>Authentication credentials (encrypted)</li>
              <li>IP address and login metadata</li>
            </ul>

            <h3 className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">B. Usage & System Data</h3>
            <p className="text-foreground mb-2">To ensure performance and security, we may collect:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Device/browser type</li>
              <li>Session activity logs</li>
              <li>Access timestamps</li>
              <li>System error logs</li>
              <li>Platform interaction metrics</li>
            </ul>

            <h3 className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">C. Client Data</h3>
            <p className="text-foreground mb-2">ORIN processes business data provided by client organizations, which may include:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Operational metrics</li>
              <li>Financial data</li>
              <li>Performance KPIs</li>
              <li>Aggregated analytics datasets</li>
            </ul>
            <p className="text-foreground mb-6">American Logics acts as a data processor for such client-provided data.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">3. How We Use Information</h2>
            <p className="text-foreground mb-2">We use collected information to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Provide and operate the ORIN platform</li>
              <li>Authenticate users</li>
              <li>Improve platform performance and features</li>
              <li>Provide analytics and decision intelligence insights</li>
              <li>Maintain system security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
            <p className="text-foreground mb-6">We do not sell personal data.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">4. Data Security</h2>
            <p className="text-foreground mb-2">American Logics implements industry-standard safeguards, including:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Role-based access controls</li>
              <li>Encrypted data storage (where applicable)</li>
              <li>Regular security monitoring</li>
              <li>Restricted internal access policies</li>
            </ul>
            <p className="text-foreground mb-6">While we apply reasonable security controls, no system is 100% secure.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">5. Data Retention</h2>
            <p className="text-foreground mb-2">We retain:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Account data as long as the user remains active</li>
              <li>Client data per contractual agreement</li>
              <li>System logs for security and compliance purposes</li>
            </ul>
            <p className="text-foreground mb-6">Upon termination of service, data is handled in accordance with client agreements.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">6. Third-Party Services</h2>
            <p className="text-foreground mb-2">ORIN may integrate with third-party services such as:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Cloud infrastructure providers</li>
              <li>Authentication providers</li>
              <li>Data integration tools</li>
            </ul>
            <p className="text-foreground mb-6">These providers process data solely for service delivery and under appropriate contractual safeguards.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">7. User Rights</h2>
            <p className="text-foreground mb-2">Depending on jurisdiction (e.g., U.S., GDPR regions), users may have rights to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Access personal data</li>
              <li>Request correction</li>
              <li>Request deletion (subject to contractual and legal requirements)</li>
              <li>Restrict processing</li>
            </ul>
            <p className="text-foreground mb-6">Requests may be submitted to: <a href="mailto:privacy@americanlogics.com" className="text-primary hover:text-primary/80">privacy@americanlogics.com</a></p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">8. Cookies & Tracking</h2>
            <p className="text-foreground mb-2">ORIN may use limited session-based cookies to:</p>
            <ul className="list-disc pl-6 mb-4 text-foreground space-y-1">
              <li>Maintain login sessions</li>
              <li>Improve security</li>
              <li>Monitor performance</li>
            </ul>
            <p className="text-foreground mb-6">We do not use ORIN for advertising or behavioral tracking.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">9. Children's Privacy</h2>
            <p className="text-foreground mb-6">ORIN is intended for business users and is not directed to individuals under 18 years of age.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">10. International Data Transfers</h2>
            <p className="text-foreground mb-6">If ORIN data is processed outside the United States, appropriate safeguards are implemented to protect data in accordance with applicable laws.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-foreground mb-6">We may update this Privacy Policy periodically. Updates will be reflected by the "Last Updated" date above. Continued use of ORIN after changes constitutes acceptance of the updated policy.</p>

            <h2 className="text-2xl font-heading font-semibold text-foreground mt-8 mb-4">12. Contact Information</h2>
            <p className="text-foreground mb-2">
              <strong>American Logics LLC</strong><br />
              Email: <a href="mailto:privacy@americanlogics.com" className="text-primary hover:text-primary/80">privacy@americanlogics.com</a><br />
              Website: <a href="http://www.americanlogics.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80">www.americanlogics.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;